/**
 * The Engine ties sources together. Each registered source carries its own
 * adapter (linkers + backend + executor); `ask()` targets one active source and
 * builds a parser with that source's linkers. Adding a source — an uploaded
 * ontology, another SPARQL endpoint — is the whole extension story for BYO-data
 * and (with a multi-source planner) federation.
 */
import { DeterministicParser } from "../analysis/deterministic-parser.ts";
import { analyze } from "../analysis/patterns.ts";
import { FallbackParser } from "../parser/fallback-parser.ts";
import { LlmParser, type CompleteFn } from "../parser/llm-parser.ts";
import type { SemanticParser } from "../parser/types.ts";
import { GenericBackend } from "../backends/generic-backend.ts";
import { WikidataBackend } from "../backends/wikidata-backend.ts";
import type { SparqlBackend } from "../backends/types.ts";
import { FetchSparqlExecutor } from "../execute/fetch-executor.ts";
import { LocalStoreEvaluator } from "../execute/local-store-evaluator.ts";
import type { QueryExecutor } from "../execute/types.ts";
import { WikidataEntityLinker, WikidataRelationLinker } from "../linking/wikidata.ts";
import { LocalEntityLinker, LocalRelationLinker } from "../linking/local.ts";
import type { EntityLinker, RelationLinker } from "../linking/types.ts";
import { ask, type AskResult } from "../orchestrate/ask.ts";
import type { ParseContext, ParseOverrides } from "../parser/types.ts";
import type { LocalOntology } from "../rdf/ontology.ts";
import { SingleSourceFederationPlanner, type FederationPlanner } from "./federation-planner.ts";
import { InMemorySourceRegistry, WIKIDATA_ENDPOINT } from "./registry.ts";
import type { Source } from "./types.ts";

export interface SourceAdapter {
  source: Source;
  entityLinker: EntityLinker;
  relationLinker: RelationLinker;
  backend: SparqlBackend;
  executor: QueryExecutor;
}

export interface AskEngineOptions {
  activeSourceId?: string;
  overrides?: ParseOverrides;
  signal?: AbortSignal;
  maxAlternatives?: number;
}

export class Engine {
  readonly registry = new InMemorySourceRegistry();
  planner: FederationPlanner = new SingleSourceFederationPlanner();
  /** Set to enable the optional LLM fallback (BYOK or server-held). */
  llmComplete?: CompleteFn;
  private readonly adapters = new Map<string, SourceAdapter>();

  addSource(adapter: SourceAdapter): void {
    this.registry.register(adapter.source);
    this.adapters.set(adapter.source.id, adapter);
  }

  removeSource(id: string): void {
    this.adapters.delete(id);
    this.registry.remove(id);
  }

  listSources(): Source[] {
    return this.registry.list();
  }

  getAdapter(id: string): SourceAdapter | undefined {
    return this.adapters.get(id);
  }

  /**
   * Auto source routing: parse the question once (templates are source-agnostic)
   * then, for each source, try to link its entity + relation and score the fit.
   * The best-fitting source wins — this is "the system knows which source to look
   * into." Returns the default source when nothing links anywhere.
   */
  async routeSource(question: string, signal?: AbortSignal): Promise<{ sourceId: string; scores: Array<{ sourceId: string; score: number }> }> {
    const match = analyze(question);
    const scores: Array<{ sourceId: string; score: number }> = [];
    if (match) {
      for (const [id, adapter] of this.adapters) {
        let score = 0;
        if (match.entityMention) {
          const c = (await adapter.entityLinker.link(match.entityMention, { limit: 1, ...(signal ? { signal } : {}) }))[0];
          score += c ? c.score : -0.5;
        }
        if (match.relationPhrase) {
          const c = (await adapter.relationLinker.link(match.relationPhrase, { limit: 1, ...(signal ? { signal } : {}) }))[0];
          score += c ? c.score : -0.5;
        }
        scores.push({ sourceId: id, score });
      }
    }
    scores.sort((a, b) => b.score - a.score);
    const best = scores[0];
    return {
      sourceId: best && best.score > 0 ? best.sourceId : this.registry.defaultSource().id,
      scores,
    };
  }

  async ask(question: string, opts: AskEngineOptions = {}): Promise<AskResult> {
    let sourceId = opts.activeSourceId;
    if (sourceId === "auto") {
      sourceId = (await this.routeSource(question, opts.signal)).sourceId;
    }
    const source = (sourceId ? this.registry.get(sourceId) : undefined) ?? this.registry.defaultSource();
    const adapter = this.adapters.get(source.id);
    if (!adapter) throw new Error(`No adapter registered for source "${source.id}".`);

    const deterministic = new DeterministicParser({
      entityLinker: adapter.entityLinker,
      relationLinker: adapter.relationLinker,
      sourceId: source.id,
    });
    let parser: SemanticParser = deterministic;
    if (this.llmComplete) {
      const llm = new LlmParser({
        complete: this.llmComplete,
        entityLinker: adapter.entityLinker,
        relationLinker: adapter.relationLinker,
        sourceId: source.id,
      });
      parser = new FallbackParser(deterministic, llm);
    }

    const ctx: ParseContext = {};
    if (opts.signal) ctx.signal = opts.signal;
    if (opts.overrides) ctx.overrides = opts.overrides;

    return ask(
      question,
      {
        parser,
        registry: this.registry,
        planner: this.planner,
        backendFor: (s) => this.adapters.get(s.id)?.backend,
        executorFor: (s) => {
          const a = this.adapters.get(s.id);
          if (!a) throw new Error(`No executor for source "${s.id}".`);
          return a.executor;
        },
        ...(opts.maxAlternatives !== undefined ? { maxAlternatives: opts.maxAlternatives } : {}),
      },
      ctx,
    );
  }
}

// ---- Adapter factories ---------------------------------------------------

export function wikidataAdapter(opts: { userAgent?: string; isDefault?: boolean } = {}): SourceAdapter {
  return {
    source: {
      id: "wikidata",
      label: "Wikidata",
      kind: "sparql-endpoint",
      endpoint: WIKIDATA_ENDPOINT,
      backendId: "wikidata",
      isDefault: opts.isDefault ?? true,
      description: "The Wikidata Query Service — the whole of Wikidata's knowledge graph.",
    },
    entityLinker: new WikidataEntityLinker(),
    relationLinker: new WikidataRelationLinker(),
    backend: new WikidataBackend(),
    executor: new FetchSparqlExecutor(opts.userAgent ? { userAgent: opts.userAgent } : {}),
  };
}

export function localOntologyAdapter(
  id: string,
  label: string,
  ontology: LocalOntology,
): SourceAdapter {
  return {
    source: {
      id,
      label,
      kind: "local-store",
      backendId: "generic",
      description: `Uploaded ontology · ${ontology.stats.triples.toLocaleString()} triples, ${ontology.stats.entities} entities, ${ontology.stats.properties} properties.`,
    },
    entityLinker: new LocalEntityLinker(id, ontology.entityIndex),
    relationLinker: new LocalRelationLinker(id, ontology.propertyIndex),
    backend: new GenericBackend(ontology.prefixes),
    executor: new LocalStoreEvaluator(ontology.store),
  };
}

// Endpoints registered by the user have no NL linker (no wbsearchentities-style
// service), so NL questions can't be grounded against them — they are query
// targets for the SPARQL editor (incl. federated SERVICE queries).
class NullEntityLinker implements EntityLinker {
  readonly sourceId: string;
  constructor(sourceId: string) {
    this.sourceId = sourceId;
  }
  async link(): Promise<[]> {
    return [];
  }
}
class NullRelationLinker implements RelationLinker {
  readonly sourceId: string;
  constructor(sourceId: string) {
    this.sourceId = sourceId;
  }
  async link(): Promise<[]> {
    return [];
  }
}

export function endpointAdapter(
  id: string,
  label: string,
  endpoint: string,
  opts: { userAgent?: string } = {},
): SourceAdapter {
  return {
    source: {
      id,
      label,
      kind: "sparql-endpoint",
      endpoint,
      backendId: "generic",
      description: `SPARQL endpoint · ${endpoint}`,
    },
    entityLinker: new NullEntityLinker(id),
    relationLinker: new NullRelationLinker(id),
    backend: new GenericBackend(),
    executor: new FetchSparqlExecutor(opts.userAgent ? { userAgent: opts.userAgent } : {}),
  };
}

/** A ready-to-use engine with Wikidata registered as the default source. */
export function createEngine(opts: { userAgent?: string } = {}): Engine {
  const engine = new Engine();
  engine.addSource(wikidataAdapter(opts.userAgent ? { userAgent: opts.userAgent } : {}));
  return engine;
}
